import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useExperiments } from '../../hooks/useExperiment';
import { useServedRegion } from '../../hooks/useServedRegion';
import { getBackendApiUrl } from '../../config/backend';
import styles from './AuthDisabledBanner.module.css';

interface SystemUser {
  email: string;
  role: string;
}

/**
 * TRAC-478: Unified dev-info pill.
 *
 * Consolidates what used to be split across the floating auth-off banner and
 * the top-of-chat ExperimentBadge into a single corner pill so the chat area
 * above can be laid out with nothing pinned above it. Stakeholders felt the
 * top banners were distracting and, practically, the extra header elements
 * were interfering with the starter-prompt viewport math.
 *
 * Rendered only in the outer portal frame (App.tsx gates /embed) so there's
 * no duplicate when the portal iframes /embed. The outer frame reads auth
 * from its own AuthProvider, experiments from shared localStorage, and
 * region from /health — all the same data the iframe would see.
 *
 * Shows when EITHER auth is disabled OR there's experiment/region data to
 * surface.
 */

const AuthDisabledBanner = () => {
  const { isAuthDisabled, getToken } = useAuth();
  const { experiments, isOverride } = useExperiments();
  const { region, pinned } = useServedRegion();
  const [currentEmail, setCurrentEmail] = useState('localdev@example.com');
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [switching, setSwitching] = useState(false);
  // Which agent fleet (v1/v2) the backend is routing to. Reflects the
  // AGENT_VERSION env default and the live Admin -> Settings override.
  const [agentVersion, setAgentVersion] = useState<string | null>(null);
  // TRAC-478: Persist minimized + corner state across reloads — stakeholders
  // found it annoying that the pill popped back open on every refresh.
  const [minimized, setMinimized] = useState<boolean>(() => {
    return localStorage.getItem('traci_dev_pill_minimized') === 'true';
  });
  const [corner, setCorner] = useState<'br' | 'bl' | 'tr' | 'tl'>(() => {
    const stored = localStorage.getItem('traci_dev_pill_corner');
    return (stored === 'br' || stored === 'bl' || stored === 'tr' || stored === 'tl') ? stored : 'br';
  });

  useEffect(() => {
    localStorage.setItem('traci_dev_pill_minimized', String(minimized));
  }, [minimized]);

  useEffect(() => {
    localStorage.setItem('traci_dev_pill_corner', corner);
  }, [corner]);

  const fetchState = useCallback(async () => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch current mock user
      const roleRes = await fetch(`${getBackendApiUrl()}/api/users/mock-role`, { headers });
      if (roleRes.ok) {
        const data = await roleRes.json();
        setCurrentEmail(data.email);
      }

      // Fetch all users for the dropdown (uses mock-users endpoint, no admin required)
      const usersRes = await fetch(`${getBackendApiUrl()}/api/users/mock-users`, { headers });
      if (usersRes.ok) {
        const data = await usersRes.json();
        const userList = (Array.isArray(data) ? data : data.users || [])
          .sort((a: any, b: any) => {
            // Sort: Admin first, then QA, then User
            const order: Record<string, number> = { Admin: 0, QA: 1, User: 2 };
            return (order[a.role] ?? 3) - (order[b.role] ?? 3) || a.email.localeCompare(b.email);
          });
        setUsers(userList);
      }
    } catch {
      // Fallback — no users loaded, just show the role dropdown
    }
  }, [getToken]);

  useEffect(() => {
    if (isAuthDisabled) fetchState();
  }, [isAuthDisabled, fetchState]);

  // Poll the effective agent version (public endpoint) so the pill shows
  // AGENT: V1/V2 and tracks live admin toggles.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${getBackendApiUrl()}/api/settings/agent-version`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && data?.version) setAgentVersion(String(data.version));
        }
      } catch {
        // ignore — the indicator just won't render
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const switchUser = async (email: string) => {
    setSwitching(true);
    try {
      const token = await getToken();
      const res = await fetch(`${getBackendApiUrl()}/api/users/mock-role`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setCurrentEmail(email);
        window.location.reload();
      }
    } catch {
      // Ignore
    } finally {
      setSwitching(false);
    }
  };

  // Dedupe experiment keys (session parser uses lowercase, override parser uses
  // uppercase — merge can produce both { a: 2, A: 2 }).
  const experimentKeys = Object.keys(experiments);
  const dedupedExperiments: Record<string, number> = {};
  for (const [f, v] of Object.entries(experiments)) {
    dedupedExperiments[f.toUpperCase()] = v;
  }
  const experimentCode = Object.entries(dedupedExperiments)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([f, v]) => `${f.toLowerCase()}${v}`)
    .join(' ');

  const hasExperiments = experimentKeys.length > 0;
  const showRegion = Boolean(region);
  const showDevMeta = hasExperiments || showRegion;

  if (!isAuthDisabled && !showDevMeta) return null;

  const currentUser = users.find(u => u.email === currentEmail);
  const displayName = currentEmail.split('@')[0];
  const displayRole = currentUser?.role || 'Admin';

  const cornerClass = styles[`corner_${corner}`] || '';
  const bannerClass = [
    styles.banner,
    isAuthDisabled ? styles.authOff : styles.neutral,
    minimized ? styles.minimized : '',
    cornerClass,
  ].filter(Boolean).join(' ');

  const icon = (
    <svg
      className={styles.icon}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {isAuthDisabled ? (
        <>
          <path d="M19.69 14a6.9 6.9 0 0 0 .31-2V8a7 7 0 0 0-14 0v4a6.9 6.9 0 0 0 .31 2" />
          <path d="M2 16l10 6 10-6" />
          <path d="M2 12l10 6 10-6" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </>
      ) : (
        <>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12" y2="16" />
        </>
      )}
    </svg>
  );

  if (minimized) {
    return (
      <div
        className={bannerClass}
        data-testid="auth-disabled-banner"
        onClick={() => setMinimized(false)}
        title="Click to expand"
      >
        {icon}
      </div>
    );
  }

  return (
    <div
      className={bannerClass}
      data-testid="auth-disabled-banner"
    >
      <div className={`${styles.row} ${styles.headerRow}`}>
        <div className={styles.headerLeft}>
          {icon}
          {isAuthDisabled && <span>AUTH OFF</span>}
        </div>
        <div className={styles.headerRight}>
          <button
            className={styles.minimizeBtn}
            onClick={() => setCorner(c => ({ br: 'bl', bl: 'tl', tl: 'tr', tr: 'br' } as const)[c])}
            title="Move corner"
          >
            &#x2922;
          </button>
          <button className={styles.minimizeBtn} onClick={() => setMinimized(true)} title="Minimize">
            &minus;
          </button>
        </div>
      </div>
      {isAuthDisabled && (
        <div className={styles.row}>
          {users.length > 0 ? (
            <select
              className={styles.roleSelect}
              value={currentEmail}
              onChange={(e) => switchUser(e.target.value)}
              disabled={switching}
            >
              {users.map(u => (
                <option key={u.email} value={u.email}>
                  {u.email.split('@')[0]} ({u.role})
                </option>
              ))}
            </select>
          ) : (
            <span className={styles.currentUser}>{displayName} ({displayRole})</span>
          )}
        </div>
      )}
      {hasExperiments && (
        <div className={styles.row}>
          <span className={`${styles.devMetaSection} ${isOverride ? styles.override : ''}`}>
            EXP: {experimentCode}{isOverride ? ' (override)' : ''}
          </span>
        </div>
      )}
      {showRegion && (
        <div className={styles.row}>
          <span className={styles.devMetaSection}>
            REGION: {region}
            {pinned !== 'auto' && (
              <span className={styles.pinned} title={`Pinned to ${pinned} via Dev Settings`}>
                📌
              </span>
            )}
          </span>
        </div>
      )}
      {agentVersion && (
        <div className={styles.row}>
          <span className={styles.devMetaSection} title="Agent fleet the backend is routing to (env default + Admin override)">
            AGENT: {agentVersion.toUpperCase()}
          </span>
        </div>
      )}
    </div>
  );
};

export default AuthDisabledBanner;
